import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../components/AppContext'
import { supabase } from '../lib/supabase'
import { Upload, ChevronRight, CheckCircle, AlertCircle, FileText, Shield, Zap, X } from 'lucide-react'

// ── CSV Parser — handles all major UK bank formats ──
function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim())
  if (lines.length < 2) return []
  const header = lines[0].toLowerCase()
  const isMonzo    = header.includes('emoji') && header.includes('category')
  const isStarling = header.includes('spending category')

  const rows = lines.slice(1).map(line => {
    const cols = []
    let cur = '', inQ = false
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ }
      else if (line[i] === ',' && !inQ) { cols.push(cur.trim()); cur = '' }
      else cur += line[i]
    }
    cols.push(cur.trim())
    return cols
  }).filter(r => r.length >= 3)

  const transactions = []
  for (const cols of rows) {
    try {
      let date, description, amount, type
      if (isMonzo) {
        date = cols[0]; description = cols[3] || cols[13] || ''
        amount = parseFloat(cols[6]?.replace(/[£,]/g, '')) || 0
        type = amount >= 0 ? 'income' : 'expense'; amount = Math.abs(amount)
      } else if (isStarling) {
        date = cols[0]; description = cols[1] || cols[2] || ''
        amount = parseFloat(cols[4]?.replace(/[£,]/g, '')) || 0
        type = amount >= 0 ? 'income' : 'expense'; amount = Math.abs(amount)
      } else {
        date = cols[0]; description = cols[1] || ''
        const debit  = parseFloat(cols[2]?.replace(/[£,]/g, '')) || 0
        const credit = parseFloat(cols[3]?.replace(/[£,]/g, '')) || 0
        if (credit > 0) { amount = credit; type = 'income' }
        else if (debit > 0) { amount = debit; type = 'expense' }
        else {
          const signed = parseFloat(cols[2]?.replace(/[£,]/g, '')) || 0
          amount = Math.abs(signed); type = signed >= 0 ? 'income' : 'expense'
        }
      }
      // Normalise date formats: dd/mm/yyyy or yyyy-mm-dd
      let parsedDate = date
      if (date?.includes('/')) {
        const p = date.split('/')
        parsedDate = p[0].length === 4
          ? `${p[0]}-${p[1]}-${p[2]}`
          : `${p[2]}-${p[1].padStart(2,'0')}-${p[0].padStart(2,'0')}`
      }
      if (!description || !amount || amount === 0) continue
      transactions.push({ date: parsedDate, description: description.trim(), merchant: description.trim(), amount, type })
    } catch (e) { /* skip bad rows */ }
  }
  return transactions
}

export default function BankConnect() {
  const { user, profile, saveProfile, loadProfile } = useAuth()
  const navigate   = useNavigate()
  const [mode,       setMode]       = useState('choose')
  const [progress,   setProgress]   = useState('')
  const [imported,   setImported]   = useState(0)
  const [error,      setError]      = useState('')
  const [dragging,   setDragging]   = useState(false)
  const fileRef = useRef(null)

  async function processFile(file) {
    if (!file.name.match(/\.(csv|txt)$/i)) {
      setError('Please upload a CSV file downloaded from your bank app')
      return
    }
    setMode('importing'); setProgress('Reading your bank statement...'); setError('')
    try {
      const text   = await file.text()
      const parsed = parseCSV(text)
      if (parsed.length === 0) throw new Error('Could not read this file. Make sure it is a CSV exported directly from your bank.')
      setProgress(`Importing ${parsed.length} transactions...`)
      const rows = parsed.map((tx, i) => ({
        user_id:      user.id,
        date:         tx.date,
        description:  tx.description,
        merchant:     tx.merchant,
        amount:       tx.amount,
        type:         tx.type,
        source:       'csv',
        truelayer_id: `csv_${user.id}_${tx.date}_${i}_${tx.amount}`,
        category:     null,
        created_at:   new Date().toISOString(),
        updated_at:   new Date().toISOString(),
      }))
      let total = 0
      for (let i = 0; i < rows.length; i += 50) {
        const batch = rows.slice(i, i + 50)
        await supabase.from('transactions').upsert(batch, { onConflict: 'user_id,truelayer_id', ignoreDuplicates: true })
        total += batch.length; setImported(total)
        setProgress(`Imported ${total} of ${rows.length}...`)
      }
      await saveProfile({ bank_connected: true, bank_name: 'CSV Import' })
      await loadProfile(user.id)
      setMode('success'); setImported(total)
      setTimeout(() => navigate('/categorise'), 2500)
    } catch (e) {
      setError(e.message || 'Import failed. Please try again.')
      setMode('csv')
    }
  }

  function handleFile(e) {
    e.preventDefault(); setDragging(false)
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0]
    if (file) processFile(file)
  }

  // ── Importing ──
  if (mode === 'importing') return (
    <div style={WRAP}>
      <div style={{ ...CARD, textAlign:'center' }}>
        <div className="spin" style={{ width:36, height:36, margin:'0 auto 24px' }} />
        <h2 style={H1}>Importing transactions</h2>
        <p style={{ fontSize:14, color:'var(--slate)', marginBottom:16 }}>{progress}</p>
        {imported > 0 && (
          <div style={{ padding:'10px 16px', background:'var(--navy-card)', borderRadius:'var(--r)', border:'1px solid var(--navy-border)', fontSize:13, color:'var(--teal)' }}>
            {imported} transactions imported so far
          </div>
        )}
      </div>
    </div>
  )

  // ── Success ──
  if (mode === 'success') return (
    <div style={WRAP}>
      <div style={{ ...CARD, textAlign:'center' }}>
        <div style={iconWrap('#00D4AA')}><CheckCircle size={24} color="var(--navy)" /></div>
        <h2 style={H1}>{imported} transactions imported.</h2>
        <p style={{ fontSize:14, color:'var(--slate)' }}>Taking you to categorise your transactions...</p>
      </div>
    </div>
  )

  // ── Choose method ──
  if (mode === 'choose') return (
    <div style={WRAP}>
      <div style={CARD}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <h1 style={H1}>Add your transactions</h1>
          <p style={{ fontSize:14, color:'var(--slate)', lineHeight:1.6 }}>
            Upload your bank statement to see your real tax position
          </p>
        </div>

        {/* CSV — primary option */}
        <button style={{ ...OPTION_BTN, borderColor:'rgba(0,212,170,.3)' }}
          onClick={() => setMode('csv')}
          onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,170,.6)'}
          onMouseLeave={e => e.currentTarget.style.borderColor='rgba(0,212,170,.3)'}>
          <div style={iconWrap('#00D4AA', 44)}><Upload size={18} color="var(--navy)" /></div>
          <div style={{ flex:1, textAlign:'left' }}>
            <div style={{ fontWeight:600, fontSize:14, color:'var(--white)', marginBottom:3 }}>
              Upload bank statement CSV
              <span style={{ marginLeft:8, fontSize:10, background:'rgba(0,212,170,.12)', color:'var(--teal)', padding:'2px 7px', borderRadius:20 }}>FREE</span>
            </div>
            <div style={{ fontSize:12, color:'var(--slate)' }}>Download from your bank app and upload here. All UK banks supported.</div>
          </div>
          <ChevronRight size={16} color="var(--teal)" />
        </button>

        <div style={{ display:'flex', alignItems:'center', gap:10, margin:'14px 0' }}>
          <div style={{ flex:1, height:1, background:'var(--navy-border)' }} />
          <span style={{ fontSize:11, color:'var(--slate-dim)' }}>coming soon</span>
          <div style={{ flex:1, height:1, background:'var(--navy-border)' }} />
        </div>

        {/* Bank connect — coming soon */}
        <div style={{ ...OPTION_BTN, opacity:.5, cursor:'default', pointerEvents:'none' }}>
          <div style={iconWrap('rgba(122,155,181,.12)', 44)}><Zap size={18} color="var(--slate)" /></div>
          <div style={{ flex:1, textAlign:'left' }}>
            <div style={{ fontWeight:600, fontSize:14, color:'var(--white)', marginBottom:3 }}>
              Connect via Open Banking
              <span style={{ marginLeft:8, fontSize:10, background:'rgba(122,155,181,.1)', color:'var(--slate)', padding:'2px 7px', borderRadius:20 }}>COMING SOON</span>
            </div>
            <div style={{ fontSize:12, color:'var(--slate)' }}>Automatic sync directly from your bank. No manual uploads needed.</div>
          </div>
        </div>

        <div style={{ marginTop:20, display:'flex', gap:8, padding:'12px 14px', background:'rgba(0,212,170,.04)', border:'1px solid rgba(0,212,170,.1)', borderRadius:8 }}>
          <Shield size={13} color="var(--teal)" style={{ flexShrink:0, marginTop:1 }} />
          <span style={{ fontSize:11, color:'var(--slate)', lineHeight:1.6 }}>
            Your data is encrypted and stored securely. We never share it with third parties.
          </span>
        </div>
      </div>
    </div>
  )

  // ── CSV upload ──
  if (mode === 'csv') return (
    <div style={WRAP}>
      <div style={CARD}>
        <button onClick={() => setMode('choose')} style={BACK_BTN}>← Back</button>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={iconWrap('#00D4AA')}><FileText size={24} color="var(--navy)" /></div>
          <h1 style={H1}>Upload bank statement</h1>
          <p style={{ fontSize:14, color:'var(--slate)', lineHeight:1.6 }}>Download a CSV from your bank app and upload it here.</p>
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleFile}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onClick={() => fileRef.current?.click()}
          style={{ border:`2px dashed ${dragging?'var(--teal)':'var(--navy-border)'}`, borderRadius:12, padding:'36px 20px', textAlign:'center', cursor:'pointer', background:dragging?'rgba(0,212,170,.04)':'var(--navy-card)', transition:'all .15s', marginBottom:16 }}>
          <Upload size={28} color={dragging?'var(--teal)':'var(--slate-dim)'} style={{ marginBottom:12 }} />
          <div style={{ fontSize:14, fontWeight:600, color:'var(--white)', marginBottom:4 }}>
            {dragging ? 'Drop it here' : 'Tap to upload or drag and drop'}
          </div>
          <div style={{ fontSize:12, color:'var(--slate-dim)' }}>CSV files only</div>
          <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display:'none' }} onChange={handleFile} />
        </div>

        {error && (
          <div className="alert alert-red fade-in" style={{ marginBottom:16 }}>
            <AlertCircle size={13} /> {error}
            <button onClick={() => setError('')} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:0 }}><X size={12}/></button>
          </div>
        )}

        {/* How to export by bank */}
        <div style={{ border:'1px solid var(--navy-border)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', fontSize:12, fontWeight:600, color:'var(--slate)', borderBottom:'1px solid var(--navy-border)', background:'rgba(255,255,255,.02)' }}>
            How to download your statement
          </div>
          {[
            ['Monzo',    'App → Account → Transactions → Export'],
            ['Starling', 'App → Spaces → Export transactions'],
            ['Barclays', 'Online Banking → Statements → Download CSV'],
            ['HSBC',     'Online Banking → Accounts → Download → CSV'],
            ['NatWest',  'Online Banking → Transactions → Export → CSV'],
            ['Lloyds',   'Internet Banking → Account → Export transactions'],
          ].map(([bank, steps], i) => (
            <div key={bank} style={{ display:'flex', alignItems:'center', padding:'9px 14px', fontSize:12, borderBottom:i<5?'1px solid rgba(255,255,255,.04)':'none' }}>
              <span style={{ fontWeight:600, color:'var(--white)', width:72, flexShrink:0 }}>{bank}</span>
              <span style={{ color:'var(--slate-dim)' }}>{steps}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  return null
}

const WRAP = { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--navy)', padding:20, paddingTop:'max(20px, env(safe-area-inset-top))', paddingBottom:'max(20px, env(safe-area-inset-bottom))' }
const CARD = { width:'100%', maxWidth:440 }
const H1   = { fontFamily:'var(--font-display)', fontSize:26, marginBottom:8, color:'var(--white)' }
const OPTION_BTN = { display:'flex', alignItems:'center', gap:14, width:'100%', padding:'16px 18px', background:'var(--navy-card)', border:'1px solid var(--navy-border)', borderRadius:'var(--r)', cursor:'pointer', transition:'border-color .15s', textAlign:'left' }
const BACK_BTN   = { background:'none', border:'none', color:'var(--slate)', fontSize:12, cursor:'pointer', marginBottom:20, padding:0, display:'block' }
function iconWrap(bg, size=48) { return { width:size, height:size, borderRadius:size*.28, background:bg, display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:size===48?16:0, flexShrink:0 } }
